import React, { Fragment, type JSX } from 'react';
import styled, { css } from 'styled-components';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { CANCELED, CANCELING, MANUFACTURING, PACKING, PROCESSING } from 'constants/consignmentStates';
import { FontWeight } from 'lib/styles';
import { Main, Text } from 'styles/themeColors';
import { formatDateTime, currencySign } from 'utils/formatters';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import productColumns from 'constants/productColumns';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import BadgePill from 'components/Settings/ProductBadges/BadgePill';
import { addonsRow, getPillTheme, hasPostOrderShippingCostEntries } from 'utils/consignment';
import Pill from 'components/UI/Pill';
import CurrencyInput from 'components/UI/CurrencyInput';
import { getProductSkuValueField } from 'utils/orderComponents';
import AffiliateCommissionLine from 'common/AffiliateCommissionLine';
import AffiliateSummary from 'common/AffiliateSummary';

const BadgeSlot = styled.span`
    align-self: flex-start;
    margin: 0px 0px 10px 0px;
`;

const ProductImage = styled.img<any>`
  cursor: pointer;
  max-height: 96px;
  max-width: 96px;
  margin: 0 auto;
  align-self: baseline;
  object-fit: contain;
`;

const TableFooterColumn = styled.span<any>`
  width: 200px;
  height: 20px;
  font-style: normal;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const Label = styled.span<any>`
  width: 200px;
  height: 20px;
  font-style: normal;
  font-weight: bold;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const TableFooterData = styled.span<any>`
  width: 104px;
  height: 20px;
  text-align: right;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
  width: 172px;
  padding-right: 12px;
`;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  min-height: 18px;
  width: 234px;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

  ${({ deleted }) => deleted && css`
  opacity: 0.5;
`}

${({ width }) => width && css`
  width: ${width};
`}
`;

const TableData = styled.td<any>`
  text-align: center;
  vertical-align: top;

  &:first-child:not(:only-child):not([rowspan]) {
    text-align: left;
    font-weight: ${FontWeight.bold};
  }

  &:first-child:not(:only-child)[rowspan] {
    text-align: left;
  }

  &:last-child:not(:only-child)[rowspan] {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
    font-weight: ${FontWeight.bold};
  }

  ${({ color }) => color && css`
    color: ${color};
  `}

  ${({ textDecoration }) => textDecoration && css`
    text-decoration: ${textDecoration};
  `}

  ${({ fontSize }) => fontSize && css`
    font-size: ${fontSize};
  `}

  ${({ fontWeight }) => fontWeight && css`
    font-weight: ${fontWeight};
  `}

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;

const Consignment = styled.span<any>`
  height: 20px;
  font-style: normal;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const mapLabelForEntry = (type: string) => {
    switch (type) {
        case PROCESSING:
        case PACKING:
            return 'In stock';
        case MANUFACTURING:
            return 'Manufacturing';
        case CANCELED:
        case CANCELING:
            return 'Canceled';
        default:
            return '';
    }
}

interface TableValue {
    product: JSX.Element,
    quantity: JSX.Element,
    unitPrice: JSX.Element,
    subTotal: JSX.Element
};

interface Props {
    showSummary?: boolean;
    isRental?: boolean;
    consignmentEntries: ConsignmentEntry[];
    setAdditionalShippingCostsValues: (value: any) => void;
    errorMessage?: string;
    consignmentId: string;
    fullConsignmentId: string;
    state: string;
    deliveryDate?: string;
    deliveryCost: number;
    additionalShippingCostsValues: any[];
    isInvalid: boolean;
    splitConsignmentBody: any;
    fulfillmentRequest?: ConsignmentEntry[];
    country: string;
};


const onValueChange = (value: any, setAdditionalShippingCostsValues: any, additionalShippingCostsValues: any, consignmentId: string) => {
    const findConsignment = (shippingCostValues: any) => shippingCostValues.consignmentId === consignmentId;
    const consignmentExists = additionalShippingCostsValues.some(findConsignment);

    let updatedCosts;
    if (consignmentExists) {
        // Update existing consignment
        updatedCosts = additionalShippingCostsValues.map((shippingCost: any) => {
            if (findConsignment(shippingCost)) {
                return {
                    ...shippingCost,
                    additionalShippingCost: !isNaN(value) ? value : 0
                };
            }
            return shippingCost;
        });
    } else {
        // Add new consignment entry if it doesn't exist
        updatedCosts = [
            ...additionalShippingCostsValues,
            {
                consignmentId: consignmentId,
                additionalShippingCost: !isNaN(value) ? value : 0
            }
        ];
    }

    setAdditionalShippingCostsValues(updatedCosts);
}

const SplitPostOrderShippingCostRent = ({
    consignmentId,
    fullConsignmentId,
    consignmentEntries,
    errorMessage,
    state,
    deliveryDate,
    deliveryCost,
    additionalShippingCostsValues,
    setAdditionalShippingCostsValues,
    isInvalid,
    isRental,
    splitConsignmentBody,
    fulfillmentRequest,
    country
}: Props) => {

    const findConsignmentValue = additionalShippingCostsValues.find((cons: any) => cons.consignmentId === fullConsignmentId);
    const additionalShippingCost = findConsignmentValue?.additionalShippingCost || 0;

    // Check if any entry in this consignment requires post-order shipping cost
    const isShippingCostDisabled = !hasPostOrderShippingCostEntries(consignmentEntries, fulfillmentRequest) || state === CANCELING || state === CANCELED;

    const shippingCost = (
        <FlexboxContainerComponent flexDirection="column" alignItems='flex-end' width='100%'>
            <FlexboxContainerComponent padding='16px 0'>
                <Label>Shipping cost</Label>
                <TableFooterData>&nbsp;</TableFooterData>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent padding='0 0 16px 0'>
                <TableFooterColumn>Included in products</TableFooterColumn>
                <TableFooterData> ${deliveryCost}</TableFooterData>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent alignItems='center'>
                <TableFooterColumn>Added post-order</TableFooterColumn>
                <FlexboxContainerComponent margin="8px 0">
                    <CurrencyInput
                        selectAllOnFirstClick
                        id="added-post-order-id"
                        labelName=""
                        placeholder=""
                        value={typeof additionalShippingCost !== 'string' ? additionalShippingCost.toFixed(2) : additionalShippingCost}
                        onValueChange={(value: any) => onValueChange(value, setAdditionalShippingCostsValues, additionalShippingCostsValues, fullConsignmentId)}
                        width="172px"
                        isInvalid={isInvalid}
                        errorMessage={errorMessage}
                        decimalsLimit={2}
                        disabled={isShippingCostDisabled}
                    />
                </FlexboxContainerComponent>
            </FlexboxContainerComponent>
        </FlexboxContainerComponent>
    );

    const productColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
        const entryDetailsBeforeEmulate = state === MANUFACTURING ? splitConsignmentBody?.entries?.find((entry: any) => entry.entryNumber === consignmentEntry.entryNumber && entry.manufactureDate > 0) :
            splitConsignmentBody?.entries?.find((entry: any) => entry.entryNumber === consignmentEntry.entryNumber);

        const rentalStartDate = state === MANUFACTURING ? entryDetailsBeforeEmulate?.manufactureDate : entryDetailsBeforeEmulate?.rentalStartDate;
        const rentalEndDate = state === MANUFACTURING ? entryDetailsBeforeEmulate?.manufactureEndDate : entryDetailsBeforeEmulate?.rentalEndDate;

        const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;
        const pricePerUnit = consignmentEntry?.basePrice;

        return (
            <Fragment key={consignmentEntry.id}>
                <FlexboxContainerComponent flexDirection='row'>
                    <FlexboxContainerComponent padding='0 12px 0 0' flexDirection='column'>
                        {
                            consignmentEntry.badge &&
                            <BadgeSlot>
                                <BadgePill name={consignmentEntry.badge.name} colorHash={consignmentEntry.badge.colorHash} />
                            </BadgeSlot>
                        }
                        <ProductImage src={consignmentEntry.productImages && consignmentEntry.productImages.length > 0 ? process.env.REACT_APP_MEDIA_URL! + consignmentEntry.productImages[0].path : ''} />
                    </FlexboxContainerComponent>
                    <FlexboxContainerComponent flexDirection='column'>
                        <FlexboxContainerComponent justifyContent='flex-start' margin='0 0 12px' padding='0px 0 0'>
                            <TableData>{consignmentEntry.productName}</TableData>
                        </FlexboxContainerComponent>
                        <FlexboxContainerComponent flexDirection='row'>
                            <FlexboxContainerComponent flexDirection='column' gap='6px'>
                                {getProductSkuValueField(consignmentEntry, state!)}
                                {
                                    isRental &&
                                    <FlexboxContainerComponent>
                                        <TableColumn>Serial number</TableColumn>
                                        <TableColumn color={Text.primary}>{consignmentEntry.serialNumber || 'N/A'}</TableColumn>
                                    </FlexboxContainerComponent>
                                }
                                {
                                    consignmentEntry.selectableAttributes && consignmentEntry.selectableAttributes.map(atr => {
                                        const atrValue = consignmentEntry.attributeValues.find(val => val.attributeId === atr.id);
                                        return (
                                            <FlexboxContainerComponent><TableColumn>{atr.name}</TableColumn><TableColumn color={Text.primary}>{atrValue ? atrValue.name : ''}</TableColumn></FlexboxContainerComponent>
                                        )
                                    })
                                }

                                {
                                    consignmentEntry.pricebookName && consignmentEntry.pricebookId &&
                                    <FlexboxContainerComponent>
                                        <TableColumn>Pricebook name / ID</TableColumn>
                                        <TableColumn color={Text.primary}>
                                            {`${consignmentEntry.pricebookName} / ${consignmentEntry?.priceBook?.PricebookVendorId}`}
                                        </TableColumn>
                                    </FlexboxContainerComponent>
                                }
                                <FlexboxContainerComponent>
                                    <TableColumn>{"Est. ship date"}</TableColumn>
                                    <TableColumn color={Text.primary}>{deliveryDate ? formatDateTime(deliveryDate, false, false) : formatDateTime(rentalStartDate, false, false)}</TableColumn>
                                </FlexboxContainerComponent>

                                <FlexboxContainerComponent>
                                    <TableColumn>Date range</TableColumn>
                                    <TableColumn color={Text.primary}>{formatDateTime(rentalStartDate, false, false) + " - " + formatDateTime(rentalEndDate, false, false)}</TableColumn>
                                </FlexboxContainerComponent>

                                {isRental && pricePerUnit &&
                                    <FlexboxContainerComponent>
                                        <TableColumn>Price rate</TableColumn>
                                        <TableColumn color={Main.brandDarkest} fontWeight='bold'>{`${currencySign(country, pricePerUnit!)} / ${unitOfMeasurement}`}</TableColumn>
                                    </FlexboxContainerComponent>
                                }
                                {consignmentEntry.affiliateCode && consignmentEntry.commissionPercentage != null && (
                                    <AffiliateCommissionLine
                                        commissionPercentage={consignmentEntry.commissionPercentage}
                                        commissionAmount={consignmentEntry.commissionAmount || 0}
                                        customerAffiliate={consignmentEntry.customerAffiliate}
                                        country={country}
                                    />
                                )}

                            </FlexboxContainerComponent>

                        </FlexboxContainerComponent>
                    </FlexboxContainerComponent>
                </FlexboxContainerComponent>
            </Fragment>
        )
    });

    const quantityColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
        return (
            <Fragment key={consignmentEntry.id}>
                <FlexboxContainerComponent justifyContent='flex-end' >
                    <TableData>{consignmentEntry?.quantity}</TableData>
                </FlexboxContainerComponent>
            </Fragment>
        )
    });

    const unitPriceColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {

        const unitPrice = consignmentEntry.basePrice !== consignmentEntry.discountedPrice ? consignmentEntry.discountedPrice : consignmentEntry.basePrice;

        return (
            <Fragment key={consignmentEntry.id}>
                <FlexboxContainerComponent justifyContent='flex-end'>
                    {
                        <TableData>{currencySign(country, unitPrice)}</TableData>
                    }
                </FlexboxContainerComponent>
            </Fragment>
        )
    });

    const subTotalColumn = consignmentEntries && consignmentEntries.filter(x => x.parentEntryNumber === null).map(consignmentEntry => {
        const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;

        return (
            <Fragment key={consignmentEntry.id}>
                <FlexboxContainerComponent justifyContent='flex-end'>
                    <TableData>{currencySign(country, consignmentEntry.totalPrice)}</TableData>
                </FlexboxContainerComponent>
                <FlexboxContainerComponent justifyContent='flex-end' padding='16px 0 0 '>
                    {
                        consignmentEntry.surcharge > 0
                            ? <TableData rowSpan={2}>+{currencySign(country, consignmentEntry.surcharge)} <br /> surcharge</TableData>
                            : <TableData>&nbsp;</TableData>
                    }
                    {
                        consignmentEntry.plannedUsage > 0
                            ? <><br /><br /><strong>Planned usage: {consignmentEntry.plannedUsage} {unitOfMeasurement}</strong></>
                            : <span>&nbsp;</span>
                    }
                </FlexboxContainerComponent>
            </Fragment>
        )
    });

    const data: TableValue[] = [];

    consignmentEntries.filter(x => x.parentEntryNumber === null).forEach((c, index) => {
        const addonsEntries = consignmentEntries.filter(x => x.parentEntryNumber === c.entryNumber);
        let requiredAddons = addonsEntries.filter(x => x.requiredAddon);
        let optionalAddons = addonsEntries.filter(x => !x.requiredAddon);

        const requiredAddonsFragmentRows = addonsRow(requiredAddons, true, state!, country!);
        const optionalAddonsFragmentRows = addonsRow(optionalAddons, false, state!, country!);

        data.push({
            product: productColumn[index],
            quantity: quantityColumn[index],
            unitPrice: unitPriceColumn[index],
            subTotal: subTotalColumn[index]
        });

        data.push(...requiredAddonsFragmentRows);
        data.push(...optionalAddonsFragmentRows);
    });

    const pillTheme = getPillTheme(state!);

    const pill = <Pill margin="10px 10px 10px 0px" theme={pillTheme} bgColor={pillTheme} size="xsPlus" padding="4px">{mapLabelForEntry(state)}</Pill>;

    let entryDetailsBeforeEmulate = state === MANUFACTURING ? splitConsignmentBody?.entries?.find((entry: any) => entry.manufactureDate > 0) :
        splitConsignmentBody?.entries?.[0];
    const rentalStartDate = state === MANUFACTURING ? entryDetailsBeforeEmulate?.manufactureDate : entryDetailsBeforeEmulate?.rentalStartDate;
    const rentalEndDate = state === MANUFACTURING ? entryDetailsBeforeEmulate?.manufactureEndDate : entryDetailsBeforeEmulate?.rentalEndDate;

    return (
        <>
            <FlexboxContainerComponent flexDirection='column' gap='10px' margin='0px'>
                <FlexboxContainerComponent flexDirection='row' gap='10px' margin='0px' alignItems='center'>
                    <Consignment>Order # {consignmentId}</Consignment>
                    {pill}
                    {state !== CANCELED && <FlexboxContainerComponent alignItems='center'>
                        <TableColumn color={Text.primary} width={'134px'}>{"Est. ship date"}</TableColumn>
                        <TableColumn color={Text.primary} width={'134px'}>{formatDateTime(deliveryDate ? deliveryDate : rentalStartDate, false, false)}</TableColumn>
                    </FlexboxContainerComponent>
                    }
                </FlexboxContainerComponent>
                <FlexboxContainerComponent alignItems='center'>
                    <TableColumn color={Text.primary} width={'134px'}>Start rent date</TableColumn>
                    <TableColumn color={Text.primary} width={'134px'}>{formatDateTime(rentalStartDate, false, false)}</TableColumn>
                </FlexboxContainerComponent>
                <FlexboxContainerComponent alignItems='center'>
                    <TableColumn color={Text.primary} width={'134px'}>End rent date</TableColumn>
                    <TableColumn color={Text.primary} width={'134px'}>{formatDateTime(rentalEndDate, false, false)}</TableColumn>
                </FlexboxContainerComponent>
            </FlexboxContainerComponent>
            <FlexboxContainerComponent padding='0 0' justifyContent="space-between" alignItems="center">
                <p>{'Order items'}</p>
            </FlexboxContainerComponent>
            <GenericTableWrapper
                columns={productColumns}
                data={data}
                isLoading={false}
                toolbar
                overrideToolbar
                sortBy={undefined}
                wrapperWidth="100%"
            />
            {shippingCost}
            {consignmentEntries.some(e => !!e.affiliateCode && e.commissionPercentage != null) && (
                <AffiliateSummary
                    country={country}
                    totalCommission={consignmentEntries.reduce((sum, e) => sum + (e.commissionAmount || 0), 0)}
                />
            )}

        </>
    );
};

export default React.memo(SplitPostOrderShippingCostRent);
